import { useState } from 'react';
import { usePost } from '@/lib/hooks/usePosts';
import { useComments, useDeleteComment } from '@/lib/hooks/useComments';
import { useAuth } from '@/lib/hooks/useAuth';
import { useUserProfile } from '@/lib/hooks/useUserProfile';
import { useActionBar } from '@/lib/context/ActionBarContext';
import { useToastNotification } from '@/lib/hooks/useToastNotification';
import { Post } from '@/components/community/Post';
import { Comment } from '@/components/community/Comment';
import { PostListSkeleton } from '@/components/community/PostListSkeleton';
import { AddCommentDrawer } from '@/components/community/AddCommentDrawer';
import { CommentActionsDrawer } from '@/components/community/CommentActionsDrawer';
import { ActionDrawer } from '@/components/ui/action-drawer';
import { ToastNotification } from '@/components/ui/ToastNotification';
import { Banner } from '@/components/Banner';
import { formatDateTimeForList } from '@/lib/utils/date.utils';
import { getVisibleModerationStatusForViewer } from '@/lib/utils/posts.utils';
import type { CommentWithAuthor } from '@/lib/types/database';

interface PostCommentsProps {
  postId: number;
}

export function PostComments({ postId }: PostCommentsProps) {
  const { user } = useAuth();
  const { isAdmin } = useUserProfile();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isActionsDrawerOpen, setIsActionsDrawerOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedComment, setSelectedComment] =
    useState<CommentWithAuthor | null>(null);
  const {
    data: post,
    isLoading: isLoadingPost,
    error: postError,
  } = usePost(postId);
  const {
    data: comments,
    isLoading: isLoadingComments,
    error: commentsError,
  } = useComments(postId);
  const deleteComment = useDeleteComment();
  const { toast, showToast, hideToast } = useToastNotification();

  // Register action bar with "Add Comment" button (only when post is loaded)
  useActionBar(
    post && !isLoadingPost
      ? {
          visible: true,
          className: 'border-t border-slate-600',
          actions: [
            {
              key: 'add-comment',
              label: 'Add Comment',
              variant: 'secondary',
              fullWidth: true,
              onClick: () => setIsDrawerOpen(true),
            },
          ],
        }
      : null
  );

  if (isLoadingPost) {
    return (
      <div className="flex flex-col w-full max-w-4xl mx-auto px-4 pb-4">
        <PostListSkeleton />
      </div>
    );
  }

  if (postError) {
    return (
      <div className="flex justify-center items-center flex-col w-full max-w-4xl mx-auto px-4 pb-4">
        <p className="text-body text-red-400">
          Failed to load post. Please try again later.
        </p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="flex justify-center items-center flex-col w-full max-w-4xl mx-auto px-4 pb-4">
        <p className="text-body">Post not found.</p>
      </div>
    );
  }

  const userIsAdmin = isAdmin();
  const isOwner = post.user_id === user?.id;
  const isAdminOrOwner = userIsAdmin || isOwner;
  const authorName = post.author_name || 'Unknown User';
  const formattedTimestamp = formatDateTimeForList(post.created_at);

  const handleCommentMenuClick = (comment: CommentWithAuthor) => {
    setSelectedComment(comment);
    setIsActionsDrawerOpen(true);
  };

  const handleDeleteClick = () => {
    setIsActionsDrawerOpen(false);
    setIsDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedComment) {
      return;
    }

    try {
      await deleteComment.mutateAsync({
        commentId: selectedComment.id,
        postId: postId,
      });
      setIsDeleteConfirmOpen(false);
      setSelectedComment(null);
      // Show success toast
      showToast({
        title: 'Comment deleted',
        description: 'Your comment has been deleted successfully.',
        variant: 'success',
      });
    } catch (error) {
      // Show error toast
      showToast({
        title: 'Delete failed',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to delete comment. Please try again.',
        variant: 'error',
      });
      console.error('Failed to delete comment:', error);
    }
  };

  const handleCloseActionsDrawer = (open: boolean) => {
    setIsActionsDrawerOpen(open);
    if (!open) {
      setSelectedComment(null);
    }
  };

  const handleCloseDeleteConfirm = (open: boolean) => {
    setIsDeleteConfirmOpen(open);
    if (!open) {
      setSelectedComment(null);
    }
  };

  const isRejected = post.moderation_status === 'rejected' && isOwner;

  return (
    <>
      <div className="flex flex-col w-full max-w-4xl mx-auto">
        {/* Rejection Banner for Owner's Rejected Posts */}
        {isRejected && post.rejection_reason && (
          <div className="px-4 bg-slate-600">
            <Banner
              title="Not Approved"
              description={post.rejection_reason}
              variant="error"
            />
          </div>
        )}

        {/* Original Post */}
        <div className="mb-6 bg-slate-600 px-4 py-8">
          <Post
            postId={post.id}
            author={authorName}
            postTimestamp={formattedTimestamp}
            imgUrl={post.image_url || undefined}
            content={post.content}
            commentCount={post.comment_count}
            isAdminOrOwner={isAdminOrOwner}
            disableClick={true}
            moderationStatus={getVisibleModerationStatusForViewer(
              post,
              user?.id,
              userIsAdmin
            )}
            onMenuClick={() => {
              // TODO: Show post menu (edit/delete)
              console.log('Show menu for post:', post.id);
            }}
          />
        </div>

        {/* Comments Section */}
        <div className="space-y-6 px-4">
          {isLoadingComments ? (
            <div className="text-body text-slate-400">Loading comments...</div>
          ) : commentsError ? (
            <p className="text-body text-red-400">
              Failed to load comments. Please try again later.
            </p>
          ) : !comments || comments.length === 0 ? (
            <p className="text-body text-slate-400 py-8">No comments yet.</p>
          ) : (
            comments.map((comment) => {
              const commentIsOwner = comment.user_id === user?.id;
              const commentIsAdminOrOwner = userIsAdmin || commentIsOwner;
              return (
                <Comment
                  key={comment.id}
                  comment={comment}
                  isAuthor={comment.user_id === post.user_id}
                  isAdminOrOwner={commentIsAdminOrOwner}
                  onMenuClick={() => handleCommentMenuClick(comment)}
                />
              );
            })
          )}
        </div>
      </div>
      <AddCommentDrawer
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        postId={postId}
      />
      <CommentActionsDrawer
        open={isActionsDrawerOpen}
        onOpenChange={handleCloseActionsDrawer}
        onDelete={handleDeleteClick}
        isDeleting={deleteComment.isPending}
      />
      {selectedComment && (
        <ActionDrawer
          open={isDeleteConfirmOpen}
          onOpenChange={handleCloseDeleteConfirm}
          title="Delete Comment?"
          description="This comment will be permanently deleted. This action cannot be undone."
          confirmLabel="Delete"
          cancelLabel="Cancel"
          confirmVariant="destructive"
          onConfirm={handleConfirmDelete}
          isLoading={deleteComment.isPending}
          loadingLabel="Deleting..."
        />
      )}

      {/* Toast Notification */}
      <ToastNotification
        open={toast.open}
        onClose={hideToast}
        title={toast.title}
        description={toast.description}
        variant={toast.variant}
        position="bottom"
      />
    </>
  );
}
